import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { auth, firestore } from "./FirebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore";
import { currentSchoolYearEnd } from "./gradeModel";
import { resolveEntrees, resolveDrinks } from "./lunchModel";
import {
  ensureUserProfile,
  readPendingRegistration,
  clearPendingRegistration,
} from "./provisionUser";

export const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [uid, setUid]                 = useState(null);
  // Whether the signed-in user's email is verified. Google users are always true;
  // password users start false until they click the verification link. Kept in
  // state (not just read off the auth user) so a manual reload() can flip it and
  // re-render the app-wide verification gate.
  const [emailVerified, setEmailVerified] = useState(false);
  // Whether a users/{uid} profile doc exists. Lets the app grandfather existing
  // accounts past the email-verification gate (only brand-new sign-ups, which
  // have no profile yet, are held on the verify screen).
  const [hasProfile, setHasProfile] = useState(false);
  const [schoolId, setSchoolId]       = useState(null);
  const [role, setRole]               = useState("teacher");
  const [title, setTitle]             = useState("");
  const [lastName, setLastName]       = useState("");
  const [libraryEnabled, setLibraryEnabled]       = useState(false);
  const [lendingTrackers, setLendingTrackers]     = useState([]);
  const [classroomPin, setClassroomPin]           = useState("");
  const [absentBoardDefault, setAbsentBoardDefault] = useState(false);
  // Admin-only clutter pref: when true, the attendance class picker shows only
  // the admin's OWN classes instead of every class in the school.
  const [hideSchoolWideAttendance, setHideSchoolWideAttendance] = useState(false);
  // Self-serve specialist opt-in (a Settings toggle). Combined with the admin-assigned
  // `specialist` role below — EITHER enables the all-school sign-out/attendance view.
  const [specialistPref, setSpecialistPref] = useState(false);
  // How this teacher identifies students: "none" | "school" | "class" | null
  // (unset). Drives the onboarding prompt + the Add-Students directory default.
  const [studentIdScheme, setStudentIdScheme]     = useState(null);
  // Which tools the teacher chose in the onboarding wizard. Raw map on the user
  // doc ({ signOut, attendance, checklist } booleans); unset means an account
  // from before the wizard → everything on. Resolved into `features` below so
  // existing users see zero change. (library/lending keep their own gates:
  // libraryEnabled + a non-empty lendingTrackers.)
  const [rawFeatures, setRawFeatures]             = useState(null);
  // Onboarding progress: { complete } on the user doc. `needsOnboarding` is true
  // only for accounts that STARTED the new wizard but haven't finished — never for
  // pre-wizard accounts (no `onboarding` field), so nobody old gets trapped.
  const [onboarding, setOnboarding]               = useState(null);
  // Whether the sign-out screens show the per-student "times out today" (🔁 N)
  // count. Unset defaults to true (it always showed before the toggle existed).
  const [showTripsCount, setShowTripsCount]       = useState(true);
  const [allowCustomLocation, setAllowCustomLocation] = useState(true);
  const [gradeSpan, setGradeSpan]     = useState(null);
  const [lunchEnabled, setLunchEnabled] = useState(false);
  const [lunchEntrees, setLunchEntrees] = useState(resolveEntrees(null));
  const [lunchDrinks, setLunchDrinks]   = useState(resolveDrinks(null));
  const [loading, setLoading]         = useState(true);
  // Mobile nav drawer (the sidebar becomes an off-canvas drawer on phones).
  // Lives here because the NavBar (which holds the hamburger) and the Sidebar
  // (which is the drawer) sit in different parts of the tree.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Guards the once-per-session "last active" stamp (see stampActive below).
  const activeStampedRef = useRef(false);
  // Guards the once-per-uid deferred provisioning of verified password users.
  const provisionedRef = useRef(null);

  // Record that this teacher opened the app today, so the super-admin console can
  // show real engagement (last-seen + active-user counts) rather than only
  // cumulative "added" totals. Throttled: writes at most once per session, and only
  // if the stored stamp is stale (>1h), so it costs ~one write per teacher per day.
  // A no-role/no-schoolId write is allowed by the owner self-update rule.
  const stampActive = (currentUid, lastActive) => {
    if (activeStampedRef.current) return;
    activeStampedRef.current = true;
    const lastMs = lastActive?.toDate ? lastActive.toDate().getTime() : 0;
    if (Date.now() - lastMs < 60 * 60 * 1000) return; // already fresh — skip the write
    updateDoc(doc(firestore, "users", currentUid), { lastActive: serverTimestamp() }).catch(() => {});
  };

  // Track auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setUser(u || null);
      setUid(u?.uid || null);
      setEmailVerified(!!u?.emailVerified);
      if (!u) {
        setSchoolId(null);
        setRole("teacher");
        setLibraryEnabled(false);
        setLendingTrackers([]);
        setClassroomPin("");
        setMobileNavOpen(false);
        setHasProfile(false);
        provisionedRef.current = null;
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Re-check the verified flag on demand. Firebase's user.reload() does NOT
  // re-fire onAuthStateChanged, so the VerifyEmail screen calls this (on a poll
  // and via its button) to notice the link was clicked and drop the gate.
  const refreshEmailVerified = async () => {
    const cu = auth.currentUser;
    if (!cu) return false;
    try {
      await cu.reload();
      setEmailVerified(cu.emailVerified);
      return cu.emailVerified;
    } catch {
      return cu.emailVerified;
    }
  };

  // Deferred provisioning: a password account creates NO Firestore data until its
  // email is verified (bots never get here). Once verified, provision the profile
  // + provisional school + sample classroom, consuming the name fields stashed at
  // registration. Idempotent (ensureUserProfile no-ops if the doc exists) and
  // guarded per-uid. Google users are provisioned at their sign-in button, so
  // they're skipped here.
  useEffect(() => {
    if (!uid || !emailVerified) return;
    const cu = auth.currentUser;
    if (!cu) return;
    const isPasswordUser = cu.providerData?.some(p => p.providerId === "password");
    if (!isPasswordUser) return;
    if (provisionedRef.current === uid) return;
    provisionedRef.current = uid;

    const pending = readPendingRegistration();
    const extras = pending && pending.email === cu.email ? pending : {};
    ensureUserProfile(cu, extras)
      .then(() => clearPendingRegistration())
      .catch(err => {
        provisionedRef.current = null; // allow a retry on the next render
        console.error("Deferred provisioning failed:", err);
      });
  }, [uid, emailVerified]);

  // Subscribe to the user doc whenever uid changes
  useEffect(() => {
    if (!uid) return;
    activeStampedRef.current = false; // new sign-in → allow one fresh stamp
    const unsub = onSnapshot(
      doc(firestore, "users", uid),
      snap => {
        setHasProfile(snap.exists());
        if (snap.exists()) {
          stampActive(uid, snap.get("lastActive"));
          setSchoolId(snap.get("schoolId") ?? null);
          setRole(snap.get("role") ?? "teacher");
          setTitle(snap.get("title") ?? "");
          setLastName(snap.get("lastName") ?? "");
          setLibraryEnabled(snap.get("libraryEnabled") === true);
          setLendingTrackers(snap.get("lendingTrackers") || []);
          setClassroomPin(snap.get("classroomPin") || "");
          setAbsentBoardDefault(snap.get("absentBoardDefault") === true);
          setHideSchoolWideAttendance(snap.get("hideSchoolWideAttendance") === true);
          setSpecialistPref(snap.get("specialistMode") === true);
          setStudentIdScheme(snap.get("studentIdScheme") ?? null);
          setShowTripsCount(snap.get("showTripsCount") !== false);
          // Unset defaults to ON — students could always type a custom "Other"
          // destination until this toggle existed.
          setAllowCustomLocation(snap.get("allowCustomLocation") !== false);
          setRawFeatures(snap.get("features") ?? null);
          setOnboarding(snap.get("onboarding") ?? null);
        } else {
          setSchoolId(null);
          setRole("teacher");
          setLibraryEnabled(false);
          setLendingTrackers([]);
          setClassroomPin("");
        }
        setLoading(false);
      },
      err => {
        console.error("Error fetching user document:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [uid]);

  // Subscribe to the school doc for school-wide config (grade span for the
  // grade↔gradYear model). Cleared when the user has no school.
  useEffect(() => {
    if (!schoolId) {
      setGradeSpan(null);
      setLunchEnabled(false);
      setLunchEntrees(resolveEntrees(null));
      setLunchDrinks(resolveDrinks(null));
      return;
    }
    const unsub = onSnapshot(
      doc(firestore, "schools", schoolId),
      snap => {
        setGradeSpan(snap.exists() ? snap.get("gradeSpan") ?? null : null);
        setLunchEnabled(snap.exists() && snap.get("lunchEnabled") === true);
        setLunchEntrees(resolveEntrees(snap.exists() ? snap.get("lunchEntrees") : null));
        setLunchDrinks(resolveDrinks(snap.exists() ? snap.get("lunchDrinks") : null));
      },
      err => console.error("Error fetching school document:", err)
    );
    return () => unsub();
  }, [schoolId]);

  // Prefer prefix + last name ("Ms. Mack"), never the first name; fall back to displayName.
  // Treat the "Other" title option as no title (don't render the literal word).
  const usableTitle = title && title.toLowerCase() !== "other" ? title : "";
  const teacherName =
    [usableTitle, lastName].filter(Boolean).join(" ").trim() ||
    user?.displayName ||
    "";

  // Resolved feature switches the nav + dashboard tiles read. Unset (pre-wizard
  // accounts) → the three always-on tools stay on. Library/Lending mirror their
  // existing gates so there's a single source of truth for each.
  const features = {
    signOut:    rawFeatures?.signOut    !== false,
    attendance: rawFeatures?.attendance !== false,
    checklist:  rawFeatures?.checklist  !== false,
    library:    libraryEnabled,
    lending:    lendingTrackers.length > 0,
  };
  // Only accounts mid-wizard need to be sent (back) to it.
  const needsOnboarding = !!onboarding && onboarding.complete !== true;

  const value = {
    user,
    uid,
    emailVerified,
    refreshEmailVerified,
    hasProfile,
    schoolId,
    role,
    title,
    lastName,
    teacherName,
    libraryEnabled,
    lendingTrackers,
    classroomPin,
    absentBoardDefault,
    hideSchoolWideAttendance,
    // Specialist (art/music/gym/nurse/librarian) sees every class in the school in the
    // sign-out + attendance pickers. Turned on EITHER by the admin-assigned `specialist`
    // role OR the teacher's own Settings toggle (specialistMode field).
    specialistMode: role === "specialist" || specialistPref,
    studentIdScheme,
    showTripsCount,
    allowCustomLocation,
    features,
    needsOnboarding,
    gradeSpan,
    lunchEnabled,
    lunchEntrees,
    lunchDrinks,
    schoolYearEnd: currentSchoolYearEnd(),
    displayName: user?.displayName || "",
    loading,
    mobileNavOpen,
    setMobileNavOpen,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within a UserProvider");
  return ctx;
}
